import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { AuthShell, AuthField, AuthErrorBanner, OrDivider, AuthSubmitButton } from "@/components/AuthLayout";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
    const redirectUrl = window.location.origin + "/console";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const GoogleButton = ({ label }) => (
    <button
        onClick={googleLogin}
        className="w-full bg-white text-black hover:bg-white/90 font-mono uppercase text-[11px] tracking-[0.2em] py-3.5 flex items-center justify-center gap-3 transition-colors"
        data-testid="google-auth-button"
    >
        <svg width="16" height="16" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
        {label}
    </button>
);

const LoginForm = ({ email, setEmail, password, setPassword, error, busy, onSubmit }) => (
    <form onSubmit={onSubmit} className="space-y-4">
        <AuthField
            label="Email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            testId="login-email-input"
        />
        <AuthField
            label="Password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            testId="login-password-input"
        />
        <AuthErrorBanner error={error} testId="login-error" />
        <AuthSubmitButton busy={busy} testId="login-submit-button">
            Log In →
        </AuthSubmitButton>
        <div className="text-center">
            <Link
                to="/forgot-password"
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-blaze transition-colors"
                data-testid="login-forgot-link"
            >
                Forgot password?
            </Link>
        </div>
    </form>
);

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (user && typeof user === "object") navigate("/console", { replace: true });
    }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
            await login(email, password);
            navigate("/console");
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthShell
            pageTestId="login-page"
            title="Log In"
            subtitle="Back to the control room"
            footer={
                <>
                    No account?{" "}
                    <Link to="/register" className="text-blaze hover:underline" data-testid="goto-register-link">
                        Create one
                    </Link>
                </>
            }
        >
            <GoogleButton label="Continue with Google" />
            <OrDivider />
            <LoginForm
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                error={error}
                busy={busy}
                onSubmit={submit}
            />
        </AuthShell>
    );
}
